import React, { useEffect, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { safeLocal } from "../lib/safeStorage";
import { MfaSetup } from "./MfaSetup";

// 2-step sign-in reminders for regular members (enforced roles get the hard MfaGate instead):
//   • first visit: a full card;
//   • then a slim banner at most once a month, until it's turned on.
// "First shown" is per-account (dismissTip); the monthly clock is per-device.
const FIRST_TIP = "mfa-nudge-first";
const CREATOR_TIP = "mfa-nudge-creator"; // live Beacon creators get their own first card
const MONTH_MS = 30 * 24 * 3600e3;

const Pitch = ({ compact, creator }) => (
  <>
    <h3 className={`font-heading text-white ${compact ? "text-base" : "text-xl mb-2"}`}>{creator ? "Protect your Beacon and your income" : "Keep your Signal yours"}</h3>
    <p className={`font-body text-zinc-400 leading-relaxed ${compact ? "text-xs" : "text-sm mb-3"}`} data-testid={creator ? "mfa-creator-pitch" : undefined}>
      {creator && compact
        ? "Strongly suggested for your own protection: it stops anyone with your password from changing your payouts or posting as you."
        : creator
        ? "We strongly suggest this, for your own protection. Your Beacon is live: it holds your payouts, your lessons and your subscribers' trust. If someone learns your password, two-step sign-in is what stops them from changing your payout details, posting as you, or reaching the people who pay you."
        : "If someone ever learns your password, two-step sign-in is what still stops them. Without your phone, they can't read your private messages, post as you, or reach the people who trust you."}
    </p>
    {!compact && <p className="font-body text-xs text-zinc-500 leading-relaxed mb-5">It takes about a minute with a free app like <span className="text-zinc-300">Ente Auth</span> or <span className="text-zinc-300">Proton Authenticator</span>. You'll also get backup codes in case you lose your phone.</p>}
  </>
);

export const MfaNudge = () => {
  const { user, dismissTip } = useAuth();
  const [mode, setMode] = useState(null); // "card" | "banner"
  const [enrolling, setEnrolling] = useState(false);
  const creator = !!user?.beacon_handle;
  const tip = creator ? CREATOR_TIP : FIRST_TIP;

  useEffect(() => {
    if (!user || user.mfa_enabled || user.mfa_enforced) return;
    if (user.role !== "admin" && user.status !== "approved") return;
    const firstDone = (user.dismissed_tips || []).includes(`${tip}@1`);
    const last = parseInt(safeLocal.get(`ss_mfa_last_${user.id}`) || "0", 10) || 0;
    let next = null;
    if (!firstDone) next = "card";
    else if (Date.now() - last > MONTH_MS) next = "banner";
    if (!next) return;
    window.__ssMfaNudgeActive = next === "card"; // the welcome tour waits for the card
    const t = setTimeout(() => setMode(next), 900);
    return () => clearTimeout(t);
  }, [user?.id, user?.mfa_enabled, creator]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mode || user?.mfa_enabled) return null;

  const later = () => {
    safeLocal.set(`ss_mfa_last_${user.id}`, String(Date.now()));
    if (mode === "card") dismissTip(tip, 1);
    setMode(null); setEnrolling(false);
    window.__ssMfaNudgeActive = false;
  };
  const onEnrolled = () => { dismissTip(tip, 1); setMode(null); window.__ssMfaNudgeActive = false; };

  if (mode === "banner" && !enrolling) {
    return (
      <div className="fixed inset-x-0 top-16 md:top-4 z-[65] flex justify-center px-3 pointer-events-none" data-testid="mfa-nudge-banner">
        <div className="pointer-events-auto w-full max-w-xl bg-[#0d0d0d] border border-[#14A89A]/40 shadow-2xl shadow-black/60 p-3.5 flex items-start gap-3 fade-up">
          <ShieldCheck size={20} className="text-[#14A89A] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <Pitch compact creator={creator} />
            <div className="flex gap-2 mt-2.5">
              <button onClick={() => setEnrolling(true)} data-testid="mfa-banner-setup" className="whitespace-nowrap bg-[#14A89A] text-[#0A0A0A] font-semibold px-3 py-1.5 font-body text-xs">Turn it on: 1 min</button>
              <button onClick={later} data-testid="mfa-banner-later" className="whitespace-nowrap text-zinc-400 hover:text-white px-2 py-1.5 font-body text-xs">Next month</button>
            </div>
          </div>
          <button onClick={later} aria-label="Dismiss" data-testid="mfa-banner-close" className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="mfa-nudge">
      <div className="w-full max-w-md bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-5">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-[#14A89A]" />
            <span className="font-body text-[10px] tracking-[0.35em] uppercase text-[#14A89A]">Two-step sign-in</span>
          </div>
          <button onClick={later} data-testid="mfa-nudge-close" className="text-zinc-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="px-5 pb-5 pt-3">
          {!enrolling ? (
            <>
              <Pitch creator={creator} />
              <div className="flex flex-wrap gap-2.5">
                <button onClick={() => setEnrolling(true)} data-testid="mfa-nudge-setup" className="bg-[#14A89A] hover:bg-[#2BC4B5] text-[#0A0A0A] font-semibold px-5 py-2.5 font-body text-sm transition-colors">Turn it on: 1 minute</button>
                <button onClick={later} data-testid="mfa-nudge-later" className="border border-white/15 text-zinc-300 hover:bg-white/5 px-4 py-2.5 font-body text-sm transition-colors">Remind me later</button>
              </div>
            </>
          ) : (
            <MfaSetup onDone={onEnrolled} />
          )}
        </div>
      </div>
    </div>
  );
};
export default MfaNudge;
